import LabelRow from "@components/rowcomponents/LabelRow";
import { primeServices } from "@services/apis/Prime";
import {
  GetPrimeCadTradeDetailsData,
  GetPrimeCadTradeDetailsResponse,
} from "@services/models/Prime/GetPrimeCadTradeDetailsModel";
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ConstantFunctions from "../../utils/constants_functions";
import DiditKycModules from "./components/DiditKycModules";
import { UtilizationBar } from "./components/UtilizationBar";

interface PrimeCanadaUserDetailsProps {
  instanceId: string;
}

const PrimeCanadaUserDetails: React.FC<PrimeCanadaUserDetailsProps> = ({
  instanceId,
}) => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [instanceDetails, setInstanceDetails] =
    useState<GetPrimeCadTradeDetailsData | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const constantFunctions = new ConstantFunctions();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response: GetPrimeCadTradeDetailsResponse =
        await primeServices.getCadInstanceDetails({ instanceId: instanceId });

      setUserDetails(response.data.userDetails);
      setInstanceDetails(response.data);
    } catch (error: any) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [instanceId]);

  if (error) {
    return <div>Fetch error</div>;
  }

  if (isLoading) {
    return (
      <div className="border border-border-subtle rounded-md bg-bg-base mt-[20px]">
        <Skeleton height={100} />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-[12px] mt-[20px] overflow-auto"
      style={{ height: "65dvh" }}
    >
      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          User Details:
        </p>

        <LabelRow
          label="Full Name"
          value={
            instanceDetails?.fullName ??
            (userDetails?.firstName && userDetails?.lastName
              ? userDetails.firstName + " " + userDetails.lastName
              : "- -")
          }
          isCopy={
            !!instanceDetails?.fullName ||
            !!userDetails?.firstName ||
            !!userDetails?.lastName
          }
        />

        <LabelRow
          label="Email"
          value={instanceDetails?.emailId ?? userDetails?.email ?? "- -"}
          isCopy={!!instanceDetails?.emailId || !!userDetails?.email}
        />

        <LabelRow
          label="Date of Birth"
          value={
            instanceDetails?.dateOfBirth
              ? constantFunctions.dateFormatInDDMMYY(
                  instanceDetails.dateOfBirth,
                )
              : userDetails?.dob
                ? constantFunctions.dateFormatInDDMMYY(userDetails.dob)
                : "- -"
          }
        />

        <LabelRow
          label="Country"
          value={instanceDetails?.nationality ?? userDetails?.country ?? "- -"}
          valueComp={
            <div className="flex flex-row gap-[4px] items-center">
              {instanceDetails?.userCountryImage && (
                <img
                  src={instanceDetails.userCountryImage}
                  height={12}
                  width={12}
                  alt=""
                />
              )}
              <span className="text-text-base text-[12px] font-normal">
                {instanceDetails?.nationality ?? userDetails?.country ?? "- -"}
              </span>
            </div>
          }
        />
      </div>

      <div className="flex flex-col py-[8px] px-[12px] space-y-[12px] border border-border-subtle bg-bg-base rounded-lg">
        <p className="text-text-navbar-active text-[12px] font-semibold underline">
          KYC Details:
        </p>
        <DiditKycModules kycDetails={instanceDetails?.kycDetails} />
        <LabelRow
          label="KYC Status"
          value={instanceDetails?.kycStatus ?? "- -"}
        />
        <LabelRow
          label="Total Fiat Limit"
          alignment="col"
          valueComp={
            <UtilizationBar
              used={instanceDetails?.totalFiatLimit ?? 0}
              max={instanceDetails?.kycLimit ?? 0}
              currency={instanceDetails?.fiatCurrencySymbol ?? ""}
            />
          }
        />
        <div className="border-b border-border-input-dark-bg"></div>
        <LabelRow
          label="Full Name from Didit"
          value={instanceDetails?.fullNameFromDidit ?? "- -"}
          isCopy={!!instanceDetails?.fullNameFromDidit}
        />
        <LabelRow
          label="Date of Birth from Didit"
          value={
            instanceDetails?.dateOfBirthFromDidit
              ? constantFunctions.dateFormatInDDMMYY(
                  instanceDetails?.dateOfBirthFromDidit,
                )
              : "- -"
          }
        />
        <LabelRow
          label="Nationality"
          value={instanceDetails?.nationalityFromDidit ?? "- -"}
        />
        <LabelRow
          label="Document Number"
          value={instanceDetails?.documentNumberFromDidit ?? "- -"}
          isCopy={!!instanceDetails?.documentNumberFromDidit}
        />
        <LabelRow
          label="Document Type"
          value={instanceDetails?.documentTypeFromDidit ?? "- -"}
        />
        <LabelRow
          label="Document Front Image"
          valueComp={
            instanceDetails?.documentFrontImageUrlFromDidit ? (
              <div
                className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                onClick={() => {
                  if (instanceDetails?.documentFrontImageUrlFromDidit) {
                    window.open(
                      instanceDetails.documentFrontImageUrlFromDidit,
                      "_blank",
                    );
                  }
                }}
              >
                <span className="material-symbols-outlined text-[14px]">
                  open_in_new
                </span>
              </div>
            ) : (
              <span className="text-text-base text-[12px] font-normal">--</span>
            )
          }
        />
        <LabelRow
          label="Document Back Image"
          valueComp={
            instanceDetails?.documentBackImageUrlFromDidit ? (
              <div
                className="flex items-center justify-center p-[1px] border border-border-button-secondary rounded cursor-pointer"
                onClick={() => {
                  if (instanceDetails?.documentBackImageUrlFromDidit) {
                    window.open(
                      instanceDetails.documentBackImageUrlFromDidit,
                      "_blank",
                    );
                  }
                }}
              >
                <span className="material-symbols-outlined text-[14px]">
                  open_in_new
                </span>
              </div>
            ) : (
              <span className="text-text-base text-[12px] font-normal">--</span>
            )
          }
        />
      </div>
    </div>
  );
};

export default PrimeCanadaUserDetails;
